import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  createChart,
  createSeriesMarkers,
} from "lightweight-charts";

const parseChartTime = (rawValue) => {
  if (rawValue == null) {
    return null;
  }
  if (typeof rawValue === "number") {
    return rawValue > 1e12 ? Math.floor(rawValue / 1000) : Math.floor(rawValue);
  }
  if (typeof rawValue === "string" && /^\d+$/.test(rawValue.trim())) {
    const parsed = Number(rawValue.trim());
    return parsed > 1e12 ? Math.floor(parsed / 1000) : Math.floor(parsed);
  }
  const timestampMs = new Date(rawValue).getTime();
  return Number.isFinite(timestampMs) ? Math.floor(timestampMs / 1000) : null;
};

export default function BacktestTradeChart({
  candles = [],
  overlays = [],
  tradeMarkers = [],
  focusTime = null,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const markersRef = useRef(null);
  const overlaySeriesRefs = useRef([]);
  const [tooltip, setTooltip] = useState(null);

  const formattedCandles = useMemo(() => {
    const mapped = (candles || [])
      .map((candle) => {
        const time = parseChartTime(candle.timestamp);
        if (time == null) {
          return null;
        }
        const open = Number(candle.open);
        const high = Number(candle.high);
        const low = Number(candle.low);
        const close = Number(candle.close);
        if (![open, high, low, close].every(Number.isFinite)) {
          return null;
        }
        return {
          time,
          open,
          high,
          low,
          close,
        };
      })
      .filter(Boolean);

    const deduped = new Map();
    mapped.forEach((candle) => {
      deduped.set(candle.time, candle);
    });
    return Array.from(deduped.values()).sort((left, right) => left.time - right.time);
  }, [candles]);

  const formattedOverlays = useMemo(
    () =>
      (overlays || [])
        .map((overlay) => {
          const dedupedValues = new Map();
          (overlay.values || []).forEach((point) => {
            const time = parseChartTime(point.time);
            const value = Number(point.value);
            if (time == null || !Number.isFinite(value)) {
              return;
            }
            dedupedValues.set(time, { time, value });
          });
          return {
            ...overlay,
            values: Array.from(dedupedValues.values()).sort((left, right) => left.time - right.time),
          };
        })
        .filter((overlay) => overlay.values.length > 0),
    [overlays],
  );

  const formattedMarkers = useMemo(
    () => {
      const dedupedMarkers = new Map();
      (tradeMarkers || []).forEach((marker, index) => {
        const time = parseChartTime(marker.time);
        if (time == null) {
          return;
        }
        const key = marker.id || `${time}-${marker.position || "belowBar"}-${marker.text || ""}-${index}`;
        dedupedMarkers.set(key, {
          time,
          position: marker.position || "belowBar",
          shape: marker.shape || "circle",
          color: marker.color || "#60a5fa",
          text: marker.text || "",
        });
      });
      return Array.from(dedupedMarkers.values()).sort((left, right) => left.time - right.time);
    },
    [tradeMarkers],
  );

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#a1a1aa",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "#262626" },
        horzLines: { color: "#262626" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: "#262626",
      },
      rightPriceScale: {
        borderColor: "#262626",
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      handleScroll: true,
      handleScale: true,
    });

    const addSeriesCompat = (legacyMethod, seriesCtor, options) => {
      if (typeof chart[legacyMethod] === "function") {
        return chart[legacyMethod](options);
      }
      return chart.addSeries(seriesCtor, options);
    };

    const candleSeries = addSeriesCompat("addCandlestickSeries", CandlestickSeries, {
      upColor: "#22c55e",
      downColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
      borderVisible: false,
    });

    candleSeriesRef.current = candleSeries;
    markersRef.current = createSeriesMarkers(candleSeries, []);

    chart.subscribeCrosshairMove((param) => {
      if (!param?.time || !param?.seriesData) {
        setTooltip(null);
        return;
      }

      const candle = param.seriesData.get(candleSeries);
      if (!candle) {
        setTooltip(null);
        return;
      }

      setTooltip({
        time: param.time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      });
    });

    chartRef.current = chart;

    return () => {
      overlaySeriesRefs.current.forEach((series) => {
        if (chartRef.current && typeof chartRef.current.removeSeries === "function") {
          chartRef.current.removeSeries(series);
        }
      });
      overlaySeriesRefs.current = [];
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      markersRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!candleSeriesRef.current || !chartRef.current || formattedCandles.length === 0) {
      return;
    }
    candleSeriesRef.current.setData(formattedCandles);
    chartRef.current.timeScale().fitContent();
  }, [formattedCandles]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    overlaySeriesRefs.current.forEach((series) => {
      if (typeof chartRef.current.removeSeries === "function") {
        chartRef.current.removeSeries(series);
      }
    });
    overlaySeriesRefs.current = [];

    const addSeriesCompat = (legacyMethod, seriesCtor, options) => {
      if (typeof chartRef.current[legacyMethod] === "function") {
        return chartRef.current[legacyMethod](options);
      }
      return chartRef.current.addSeries(seriesCtor, options);
    };

    formattedOverlays.forEach((overlay) => {
      const lineSeries = addSeriesCompat("addLineSeries", LineSeries, {
        color: overlay.color || "#60a5fa",
        lineWidth: 1.5,
        crosshairMarkerVisible: false,
        lastValueVisible: false,
        priceLineVisible: false,
      });
      lineSeries.setData(overlay.values);
      overlaySeriesRefs.current.push(lineSeries);
    });
  }, [formattedOverlays]);

  useEffect(() => {
    if (!markersRef.current) {
      return;
    }
    markersRef.current.setMarkers(formattedMarkers);
  }, [formattedMarkers]);

  useEffect(() => {
    if (!chartRef.current || !focusTime || formattedCandles.length === 0) {
      return;
    }

    const targetTime = parseChartTime(focusTime);
    if (targetTime == null) {
      return;
    }

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    formattedCandles.forEach((candle, index) => {
      const distance = Math.abs(candle.time - targetTime);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    chartRef.current.timeScale().setVisibleLogicalRange({
      from: Math.max(0, closestIndex - 35),
      to: Math.min(formattedCandles.length - 1, closestIndex + 35),
    });
  }, [focusTime, formattedCandles]);

  const handleResetView = () => {
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  };

  if (!formattedCandles.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950/50 text-sm text-zinc-500">
        No chart data available for this backtest run.
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      {tooltip && (
        <div className="absolute left-3 top-3 z-10 rounded-md border border-white/10 bg-black/60 px-3 py-2 text-[10px] font-mono text-white backdrop-blur">
          <div className="flex gap-3">
            <span>O: {tooltip.open?.toFixed?.(2)}</span>
            <span>H: {tooltip.high?.toFixed?.(2)}</span>
            <span>L: {tooltip.low?.toFixed?.(2)}</span>
            <span>C: {tooltip.close?.toFixed?.(2)}</span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleResetView}
        className="absolute right-3 top-3 z-10 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[10px] text-white/80 backdrop-blur hover:text-white"
      >
        Reset view
      </button>

      <div ref={containerRef} className="h-full w-full" />
    </div>
  );
}
